import { Box } from "@ui/box/Box"
import { Typo } from "@ui/text/Typo"
import { FC } from "react"
import { getCardStatusDisplayLabel } from "src/utils/cardStatus"

export type StatusBadgeType = "status" | "role" | "balance" | "transaction" | "card" | "default"
export type StatusBadgeVariant = "subtle" | "outline" | "dot"

interface StatusBadgeProps {
	status: string
	type?: StatusBadgeType
	variant?: StatusBadgeVariant
	label?: string // Optional override for displayed text
	sx?: any
}

const getStatusConfig = (status: string, type: StatusBadgeType = "default") => {
	const s = (status || "").toLowerCase()

	// Default Colors (Gray)
	let colors = {
		bg: "#F3F4F6",
		color: "#6B7280",
		border: "#E5E7EB"
	}

	// --- ROLES ---
	if (type === "role") {
		if (s.includes("admin")) return { bg: "transparent", color: "#7C3AED" } // Purple
		if (s.includes("teamlead") || s.includes("team_lead"))
			return { bg: "transparent", color: "#2563EB" } // Blue
		if (s.includes("buyer") || s.includes("user"))
			return { bg: "transparent", color: "#059669" } // Green
	}

	// --- BALANCE TYPE ---
	if (type === "balance") {
		if (s === "shared") return { bg: "#F0FDFA", color: "#0D9488", border: "#99F6E4" } // Teal
		return { bg: "#F3E8FF", color: "#7E22CE", border: "#E9D5FF" } // Purple for personal/others
	}

	// --- TRANSACTION DIRECTION ---
	if (type === "transaction") {
		if (s === "income") return { bg: "#ECFDF5", color: "#059669", border: "#A7F3D0" } // Green
		if (s === "expense") return { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" } // Red
	}

	// --- GENERIC STATUSES ---
	// success / active / approved
	if (["active", "success", "approved", "completed", "used", "paid"].includes(s)) {
		colors = { bg: "#ECFDF5", color: "#059669", border: "#A7F3D0" }
	}
	// warning / pending
	else if (
		["pending", "sent", "processing", "waiting", "frozen", "created"].includes(s)
	) {
		colors = { bg: "#FFFBEB", color: "#D97706", border: "#FDE68A" } // Amber/Orange
	}
	// error / danger / inactive
	else if (
		[
			"blocked",
			"failed",
			"error",
			"declined",
			"inactive",
			"disabled",
			"rejected",
			"closed"
		].includes(s)
	) {
		colors = { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" }
	}
	// info / blue
	else if (["opened", "new", "draft"].includes(s)) {
		colors = { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" }
	}

	return colors
}

export const StatusBadge: FC<StatusBadgeProps> = ({
	status,
	type = "default",
	variant = "subtle",
	label,
	sx
}) => {
	const { bg, color, border } = getStatusConfig(status, type)
	const text =
		label ||
		(type === "card"
			? getCardStatusDisplayLabel(status)
			: status
				? status.replaceAll("_", " ")
				: "N/A")

	if (variant === "dot") {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: "6px",
					width: "fit-content",
					...sx
				}}
			>
				<Box
					sx={{
						width: "8px",
						height: "8px",
						borderRadius: "50%",
						backgroundColor: color
					}}
				/>
				<Typo
					sx={{
						color: "#374151",
						fontSize: "12px",
						fontWeight: 500,
						textTransform: "uppercase",
						letterSpacing: "0.02em"
					}}
				>
					{text}
				</Typo>
			</Box>
		)
	}

	const baseStyles = {
		padding: "4px 8px",
		borderRadius: "6px",
		fontWeight: 600,
		fontSize: "11px",
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		width: "fit-content",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		whiteSpace: "nowrap",
		...sx
	}

	if (variant === "outline") {
		return (
			<Box
				sx={{
					...baseStyles,
					backgroundColor: "transparent",
					color: color,
					border: `1px solid ${color}`
				}}
			>
				{text}
			</Box>
		)
	}

	// Default (Subtle)
	return (
		<Box
			sx={{
				...baseStyles,
				backgroundColor: bg,
				color: color,
				border: `1px solid ${border || "transparent"}`,
				...(type === "role" && { padding: "0", fontSize: "12px" })
			}}
		>
			{text}
		</Box>
	)
}
